/* ==========================================================================
   Chrune
   Vanilla, no dependency. Every behaviour degrades cleanly: without
   JavaScript the content stays visible and the forms still work.

   No user-facing string is written in this file. Every label comes from the
   theme locales through data attributes.

   The cart drawer lives in chrune-cart.js, which loads after this file.
   ========================================================================== */

(function () {
  'use strict';

  var motion = document.body.getAttribute('data-motion') || 'full';

  function reduced() {
    return motion === 'none' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function readLabels(btn) {
    var d = btn ? btn.dataset : {};
    return {
      add: d.labelAdd || '',
      soldOut: d.labelSoldOut || '',
      unavailable: d.labelUnavailable || '',
      added: d.messageAdded || '',
      error: d.messageError || '',
      engraving: d.messageEngraving || ''
    };
  }

  function cartRootUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return '/';
  }

  // Cart changes Shopify has not answered yet, counted here and in
  // chrune-cart.js, so checkout can wait for a quantity typed a moment before.
  var cartPending = 0;
  window.chruneCartPending = {
    start: function () { cartPending += 1; },
    end: function () { cartPending = Math.max(0, cartPending - 1); }
  };

  /* ---------------------------------------------------------------- Reveal */

  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!els.length) return;

    if (reduced() || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });

    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------------------------------------------------------------- Header */

  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;

    var sentinel = document.createElement('div');
    sentinel.style.cssText = 'position:absolute;top:0;height:1px;width:1px;pointer-events:none';
    document.body.prepend(sentinel);

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        header.classList.toggle('is-stuck', !entries[0].isIntersecting);
      }).observe(sentinel);
    }
  }

  /* ------------------------------------------------------------- Mobile nav */

  var overlay = document.querySelector('[data-overlay]');

  function initMobileNav() {
    var nav = document.querySelector('[data-mobile-nav]');
    var openBtn = document.querySelector('[data-menu-open]');
    var closeBtn = document.querySelector('[data-menu-close]');
    if (!nav || !openBtn) return;

    function open() {
      openBtn.setAttribute('aria-expanded', 'true');
      nav.classList.add('is-open');
      nav.setAttribute('aria-hidden', 'false');
      document.body.classList.add('is-locked');
      if (overlay) {
        overlay.hidden = false;
        void overlay.offsetWidth;
        overlay.classList.add('is-active');
      }
      var first = nav.querySelector('button, a[href]');
      if (first) first.focus({ preventScroll: true });
    }

    function close() {
      if (!nav.classList.contains('is-open')) return;
      openBtn.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
      nav.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('is-locked');
      if (overlay) {
        overlay.classList.remove('is-active');
        window.setTimeout(function () { overlay.hidden = true; }, 220);
      }
      openBtn.focus({ preventScroll: true });
    }

    openBtn.addEventListener('click', open);
    if (closeBtn) closeBtn.addEventListener('click', close);
    nav.addEventListener('click', function (e) { if (e.target.closest('a')) close(); });
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  }

  /* ----------------------------------------------------------------- Search */

  function initSearch() {
    var toggle = document.querySelector('[data-search-toggle]');
    var panel = document.querySelector('[data-search-panel]');
    var input = document.querySelector('[data-search-input]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', function () {
      var open = panel.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open && input) input.focus({ preventScroll: true });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && panel.classList.contains('is-open')) {
        panel.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus({ preventScroll: true });
      }
    });
  }

  /* ---------------------------------------------------------------- Gallery */

  function initGallery(root) {
    var viewport = root.querySelector('[data-gallery-viewport]');
    if (!viewport) return null;

    var slides = Array.prototype.slice.call(viewport.querySelectorAll('[data-gallery-slide]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-gallery-thumb]'));
    if (slides.length < 2) return null;

    // The section writes on a slide the option values its photograph shows, as
    // position=value pairs: "2=gold|1=5 mm". How many pairs, and which options
    // they name, is the product's business and not this file's.
    //
    // Nothing is ever taken out of the gallery. Every photograph of the piece
    // stays in the thumbnail column and stays reachable; a choice of options
    // only moves the frame onto the photograph that answers it.
    var filtering = !!root.querySelector('[data-gallery-variant-filter]');

    var rules = slides.map(function (slide) {
      var raw = (slide.getAttribute('data-gallery-match') || '').trim();
      if (!raw) return [];
      return raw.split('|').map(function (part) {
        var at = part.indexOf('=');
        return { position: part.slice(0, at), value: part.slice(at + 1) };
      });
    });

    var lastKey = null;
    var current = 0;

    function setActive(i) {
      current = i;
      thumbs.forEach(function (t, n) {
        var active = n === i;
        t.classList.toggle('is-active', active);
        if (active) { t.setAttribute('aria-current', 'true'); }
        else { t.removeAttribute('aria-current'); }
      });
    }

    function goTo(i, instant) {
      if (i < 0 || i >= slides.length) return;
      viewport.scrollTo({
        left: i * viewport.clientWidth,
        behavior: instant || reduced() ? 'auto' : 'smooth'
      });
    }

    thumbs.forEach(function (t, i) {
      t.addEventListener('click', function () { goTo(i); setActive(i); });
    });

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) setActive(slides.indexOf(entry.target));
        });
      }, { root: viewport, threshold: 0.6 });
      slides.forEach(function (s) { io.observe(s); });
    }

    setActive(0);

    function fits(i, selection) {
      return rules[i].every(function (r) { return selection[r.position] === r.value; });
    }

    // The photograph that answers the most of the combination wins: one shot
    // per colour and per cord comes before one shot per colour. Between two
    // equally precise ones the latest added wins, so the reference for a
    // combination is the plain shot of the piece rather than an older studio
    // or worn photograph. A photograph naming no option is a last resort.
    // Returns -1 when nothing answers the combination.
    function pick(selection) {
      var best = -1;
      var bestScore = -1;
      var plain = -1;

      for (var i = 0; i < slides.length; i++) {
        if (!fits(i, selection)) continue;
        if (!rules[i].length) {
          if (plain === -1) plain = i;
          continue;
        }
        if (rules[i].length >= bestScore) {
          bestScore = rules[i].length;
          best = i;
        }
      }

      return best !== -1 ? best : plain;
    }

    return {
      // Called on every option change with the values currently picked, keyed
      // by option position. The work is done only when the selection really
      // changed, so re-rendering the same choice moves nothing.
      setSelection: function (selection) {
        if (!filtering) return;

        var key = Object.keys(selection).sort().map(function (p) {
          return p + '=' + selection[p];
        }).join('|');
        if (key === lastKey) return;

        var firstPaint = lastKey === null;
        lastKey = key;

        // Opening the page shows the piece as the merchant arranged it: every
        // photograph in the column, the lead one in the frame. Only a real
        // choice moves the frame.
        if (firstPaint) return;

        var i = pick(selection);
        if (i === -1) return;

        goTo(i, true);
        setActive(i);
      },

      // A variant image assigned in Shopify keeps leading the gallery of a
      // piece that carries no map. Where a map exists it is the only source of
      // truth: one image per variant cannot describe a piece photographed per
      // colour and per width, and letting it through would pull the gallery
      // off the combination the customer just picked.
      showImage: function (id) {
        if (!id || filtering) return;
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].dataset.imageId === String(id)) { goTo(i); setActive(i); return; }
        }
      }
    };
  }

  /* -------------------------------------------------------------- Quantity */

  function initQuantity(root) {
    var input = root.querySelector('[data-qty-input]');
    if (!input) return;
    root.querySelectorAll('[data-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var step = parseInt(btn.dataset.qty, 10);
        var val = parseInt(input.value, 10);
        if (isNaN(val)) val = 1;
        input.value = Math.max(1, val + step);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ------------------------------------------------------------- Engraving */

  function initEngraving(root) {
    var box = root.querySelector('[data-engrave]');
    if (!box) return null;

    var fields = Array.prototype.slice.call(box.querySelectorAll('[data-engrave-field]'));
    var preview = box.querySelector('[data-engrave-preview]');
    var emptyChip = box.querySelector('[data-engrave-empty]');
    var maxChars = parseInt(box.dataset.maxChars, 10);
    var overMessage = box.dataset.messageTooLong || '';

    // A symbol is typed into the field as its file code, M02 for instance, but
    // the manufacturer engraves it as a single character. Length is therefore
    // measured in units: a known symbol code, or one ordinary character. With
    // no symbol keyboard the list is empty and a unit is simply a character,
    // so every other piece counts exactly as it did before.
    var codes = [];
    var glyphs = {};

    Array.prototype.forEach.call(box.querySelectorAll('[data-symbol]'), function (key) {
      var code = key.getAttribute('data-symbol');
      var image = key.querySelector('img');
      if (!code || codes.indexOf(code) !== -1) return;
      codes.push(code);
      if (image) glyphs[code] = image.getAttribute('src');
    });

    codes.sort(function (a, b) { return b.length - a.length; });

    function unitsOf(value) {
      var units = [];
      var i = 0;
      while (i < value.length) {
        var matched = '';
        for (var c = 0; c < codes.length; c++) {
          if (value.substr(i, codes[c].length) === codes[c]) { matched = codes[c]; break; }
        }
        if (matched) { units.push(matched); i += matched.length; }
        else { units.push(value.charAt(i)); i += 1; }
      }
      return units;
    }

    function lengthOf(value) {
      return unitsOf(value).length;
    }

    // The preview is what the customer reads back before ordering, so a code
    // is drawn as its symbol and never as the letters the field carries. Text
    // stays text, and the order is the order of the field. The images are the
    // keyboard's own, so there is a single source for them.
    function paintChip(chip, value) {
      if (chip.getAttribute('data-value') === value) return;
      chip.setAttribute('data-value', value);
      chip.textContent = '';

      var run = '';
      var hasGlyph = false;

      function flushRun() {
        if (!run) return;
        chip.appendChild(document.createTextNode(run));
        run = '';
      }

      unitsOf(value).forEach(function (unit) {
        if (!glyphs[unit]) {
          run += unit;
          return;
        }
        flushRun();
        hasGlyph = true;
        var glyph = document.createElement('img');
        glyph.className = 'engrave__glyph';
        glyph.src = glyphs[unit];
        glyph.alt = '';
        chip.appendChild(glyph);
      });

      flushRun();
      chip.classList.toggle('has-glyph', hasGlyph);
    }

    // Chips are reused between keystrokes. Recreating the node on every
    // character would replay the animation and make the preview flicker.
    function refreshPreview() {
      if (!preview) return;

      var values = [];
      fields.forEach(function (field) {
        if (field.hidden) return;
        var input = field.querySelector('[data-engrave-input]');
        if (input && input.value.trim()) values.push(input.value.trim());
      });

      var chips = Array.prototype.slice.call(preview.querySelectorAll('[data-chip]'));

      values.forEach(function (val, i) {
        var chip = chips[i];
        if (!chip) {
          chip = document.createElement('span');
          chip.className = 'engrave__chip is-entering';
          chip.setAttribute('data-chip', '');
          preview.appendChild(chip);
        }
        paintChip(chip, val);
      });

      chips.slice(values.length).forEach(function (c) { c.remove(); });

      if (emptyChip) emptyChip.hidden = values.length > 0;
    }

    function refreshCount(field) {
      var input = field.querySelector('[data-engrave-input]');
      var counter = field.querySelector('[data-engrave-count]');
      if (!input || !counter) return;
      var len = lengthOf(input.value);
      counter.textContent = maxChars ? len + '/' + maxChars : String(len);
      counter.classList.toggle('is-over', !!maxChars && len > maxChars);
    }

    // The native maxlength cannot count a three character code as one, so the
    // ceiling is held here instead: anything past the limit is trimmed off the
    // end, exactly as the browser would have done.
    function clamp(input) {
      if (!maxChars) return;
      var units = unitsOf(input.value);
      if (units.length <= maxChars) return;
      var caret = input.selectionStart;
      input.value = units.slice(0, maxChars).join('');
      if (typeof caret === 'number') {
        caret = Math.min(caret, input.value.length);
        try { input.setSelectionRange(caret, caret); } catch (e) {}
      }
    }

    fields.forEach(function (field) {
      var input = field.querySelector('[data-engrave-input]');
      if (!input) return;
      input.addEventListener('input', function () {
        clamp(input);
        refreshCount(field);
        refreshPreview();
        if (input.getAttribute('aria-invalid') === 'true') {
          var stillBad = (input.hasAttribute('required') && !input.value.trim()) ||
            (maxChars && lengthOf(input.value) > maxChars);
          if (!stillBad) input.setAttribute('aria-invalid', 'false');
        }
      });
      refreshCount(field);
    });

    refreshPreview();

    return {
      max: maxChars,
      length: lengthOf,

      syncToOptions: function (values) {
        var count = null;

        values.forEach(function (val) {
          var lower = String(val).toLowerCase();
          if (lower.indexOf('no engraving') !== -1 || lower.indexOf('sans gravure') !== -1) {
            count = 0;
            return;
          }
          var m = lower.match(/^(\d+)\s/);
          if (m) {
            var n = parseInt(m[1], 10);
            if (!isNaN(n) && (count === null || n < count)) count = n;
          }
        });

        if (count === null) return;

        box.hidden = count === 0;

        fields.forEach(function (field, i) {
          var show = i < count;
          field.hidden = !show;
          var input = field.querySelector('[data-engrave-input]');
          if (!input) return;
          if (!show) {
            input.value = '';
            input.removeAttribute('required');
            input.disabled = true;
          } else {
            input.disabled = false;
            if (i === 0) input.setAttribute('required', 'required');
          }
        });

        refreshPreview();
      },

      // An error has to name the problem: an empty required field and an
      // over-long engraving are not the same failure.
      validate: function () {
        if (box.hidden) return { ok: true, message: '' };

        var ok = true;
        var message = '';
        var firstInvalid = null;

        fields.forEach(function (field) {
          if (field.hidden) return;
          var input = field.querySelector('[data-engrave-input]');
          if (!input) return;

          var tooLong = !!maxChars && lengthOf(input.value) > maxChars;
          var missing = input.hasAttribute('required') && !input.value.trim();
          var invalid = tooLong || missing;

          input.setAttribute('aria-invalid', invalid ? 'true' : 'false');

          if (invalid && ok) {
            ok = false;
            firstInvalid = input;
            message = tooLong ? overMessage : '';
          }
        });

        if (firstInvalid) firstInvalid.focus();
        return { ok: ok, message: message };
      }
    };
  }

  /* --------------------------------------------------------- Symbol keyboard */

  function initSymbols(root, engraving) {
    var box = root.querySelector('[data-engrave]');
    if (!box) return;

    var panel = box.querySelector('[data-symbols]');
    if (!panel) return;

    var keys = Array.prototype.slice.call(panel.querySelectorAll('[data-symbol]'));
    var activeToggle = null;
    var activeInput = null;

    // One panel serves every field: it moves to the field it was opened from
    // rather than being repeated, so the page carries one set of images.
    // Hidden in the markup, so nothing opens without this script.
    panel.hidden = false;

    function full() {
      if (!engraving || !engraving.max || !activeInput) return false;
      return engraving.length(activeInput.value) >= engraving.max;
    }

    function refreshKeys() {
      var isFull = full();
      keys.forEach(function (key) { key.disabled = isFull; });
    }

    function close() {
      if (!activeToggle) return;
      panel.classList.remove('is-open');
      activeToggle.setAttribute('aria-expanded', 'false');
      activeToggle = null;
      activeInput = null;
    }

    function open(toggle) {
      var field = toggle.closest('[data-engrave-field]');
      if (!field) return;
      if (activeToggle && activeToggle !== toggle) {
        activeToggle.setAttribute('aria-expanded', 'false');
      }
      activeToggle = toggle;
      activeInput = field.querySelector('[data-engrave-input]');
      field.appendChild(panel);
      toggle.setAttribute('aria-expanded', 'true');
      refreshKeys();
      void panel.offsetWidth; // flush layout so the height transition runs
      panel.classList.add('is-open');
    }

    function insert(code) {
      if (!activeInput) return;
      var start = activeInput.selectionStart;
      var end = activeInput.selectionEnd;
      if (typeof start !== 'number') { start = activeInput.value.length; end = start; }

      var next = activeInput.value.slice(0, start) + code + activeInput.value.slice(end);
      if (engraving && engraving.max && engraving.length(next) > engraving.max) return;

      activeInput.value = next;
      var caret = start + code.length;
      activeInput.focus({ preventScroll: true });
      try { activeInput.setSelectionRange(caret, caret); } catch (e) {}
      activeInput.dispatchEvent(new Event('input', { bubbles: true }));
      refreshKeys();
    }

    box.addEventListener('click', function (e) {
      var toggle = e.target.closest('[data-symbols-toggle]');
      if (toggle) {
        if (activeToggle === toggle) { close(); } else { open(toggle); }
        return;
      }

      var key = e.target.closest('[data-symbol]');
      if (key && !key.disabled) insert(key.getAttribute('data-symbol'));
    });

    box.addEventListener('input', function (e) {
      if (e.target.closest('[data-engrave-input]')) refreshKeys();
    });

    // A different option can hide the field the panel was opened from.
    root.addEventListener('change', function (e) {
      if (e.target.closest('[data-option-input]')) close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* -------------------------------------------------------------- Variants */

  function initVariants(root, gallery, engraving) {
    var select = root.querySelector('[data-variant-select]');
    var data = root.querySelector('[data-variant-data]');
    var inputs = Array.prototype.slice.call(root.querySelectorAll('[data-option-input]'));
    if (!select || !data) return;

    var variants;
    try { variants = JSON.parse(data.textContent); } catch (e) { return; }

    root.classList.add('is-enhanced');
    select.setAttribute('aria-hidden', 'true');
    select.setAttribute('tabindex', '-1');

    var priceNow = root.querySelector('[data-price-current]');
    var priceWas = root.querySelector('[data-price-compare]');
    var addBtn = root.querySelector('[data-add-button]');
    var addLabel = root.querySelector('[data-add-label]');
    var labels = readLabels(addBtn);

    function selected() {
      return inputs.filter(function (i) { return i.checked; }).map(function (i) { return i.value; });
    }

    // What is picked, keyed by the position of the option it belongs to. This
    // is everything the gallery needs: it holds its own rules and reads the
    // values it cares about out of this.
    function selection() {
      var picked = {};
      inputs.forEach(function (input) {
        if (input.checked) picked[input.dataset.position] = String(input.value).trim().toLowerCase();
      });
      return picked;
    }

    function find(values) {
      return variants.filter(function (v) {
        return v.options.length === values.length && v.options.every(function (o, i) { return o === values[i]; });
      })[0];
    }

    function update() {
      var values = selected();

      inputs.forEach(function (input) {
        if (!input.checked) return;
        var label = root.querySelector('[data-option-selected="' + input.dataset.position + '"]');
        if (label) label.textContent = input.value;
      });

      if (engraving) engraving.syncToOptions(values);

      // Before the variant is resolved: the gallery has to follow the options
      // even when the combination asked for is not for sale.
      if (gallery) gallery.setSelection(selection());

      var variant = find(values);
      if (!variant) {
        if (addBtn) addBtn.disabled = true;
        if (addLabel) addLabel.textContent = labels.unavailable;
        return;
      }

      select.value = variant.id;
      if (priceNow) priceNow.textContent = variant.price;
      if (priceWas) {
        if (variant.compare_at_price) {
          priceWas.textContent = variant.compare_at_price;
          priceWas.hidden = false;
        } else {
          priceWas.textContent = '';
          priceWas.hidden = true;
        }
      }

      if (addBtn) addBtn.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? labels.add : labels.soldOut;
      if (gallery) gallery.showImage(variant.image_id);

      if (window.history && window.history.replaceState) {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    inputs.forEach(function (i) {
      i.addEventListener('change', update);
    });

    update();
  }

  /* ---------------------------------------------------------- Add to cart */

  function initAddToCart(root, engraving) {
    var form = root.querySelector('.pdp__form');
    var status = root.querySelector('[data-add-status]');
    var btn = root.querySelector('[data-add-button]');
    if (!form || !btn) return;

    var labels = readLabels(btn);

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      if (engraving) {
        var check = engraving.validate();
        if (!check.ok) {
          if (status) {
            status.textContent = check.message || labels.engraving;
            status.classList.add('is-error');
          }
          return;
        }
      }

      btn.disabled = true;
      btn.setAttribute('aria-busy', 'true');
      if (status) { status.textContent = ''; status.classList.remove('is-error'); }

      fetch(cartRootUrl() + 'cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      })
        .then(function (r) { if (!r.ok) throw new Error('add'); return r.json(); })
        .then(function () {
          return fetch(cartRootUrl() + 'cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          var count = document.querySelector('[data-cart-count]');
          if (count) {
            count.textContent = cart.item_count;
            count.hidden = cart.item_count === 0;
            count.classList.add('is-bumped');
            window.setTimeout(function () { count.classList.remove('is-bumped'); }, 180);
          }
          if (status) status.textContent = labels.added;
          document.dispatchEvent(new CustomEvent('chrune:cart-added'));
        })
        .catch(function () {
          if (status) {
            status.textContent = labels.error;
            status.classList.add('is-error');
          }
        })
        .finally(function () {
          btn.disabled = false;
          btn.removeAttribute('aria-busy');
        });
    });
  }

  /* -------------------------------------------------------------- Cart page */

  function initCartPage() {
    var root = document.querySelector('[data-cart-root]');
    if (!root) return;

    // A line is named by its Shopify line item key, never by its position:
    // two lines of one piece engraved differently share a variant, and a
    // position read from stale markup names whichever line sits there now. A
    // key that no longer exists is refused, and the page reloads.
    function change(key, quantity) {
      if (!key) return;
      root.classList.add('is-loading');
      var sections = [root.dataset.sectionId, 'cart-drawer-items'].filter(Boolean).join(',');
      window.chruneCartPending.start();

      fetch(cartRootUrl() + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: String(key),
          quantity: quantity,
          sections: sections,
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { if (!r.ok) throw new Error('cart'); return r.json(); })
        .then(function (cart) {
          var count = document.querySelector('[data-cart-count]');
          if (count) {
            count.textContent = cart.item_count;
            count.hidden = cart.item_count === 0;
          }
          if (cart.sections && cart.sections[root.dataset.sectionId]) {
            var fresh = new DOMParser()
              .parseFromString(cart.sections[root.dataset.sectionId], 'text/html')
              .querySelector('[data-cart-root]');
            if (fresh) root.innerHTML = fresh.innerHTML;
          }
          var drawerBody = document.querySelector('[data-cart-drawer-body]');
          if (drawerBody && cart.sections && cart.sections['cart-drawer-items']) {
            var c = new DOMParser()
              .parseFromString(cart.sections['cart-drawer-items'], 'text/html')
              .querySelector('[data-cart-drawer-content]');
            if (c) drawerBody.innerHTML = c.outerHTML;
          }
        })
        .catch(function () { window.location.reload(); })
        .finally(function () {
          window.chruneCartPending.end();
          root.classList.remove('is-loading');
        });
    }

    root.addEventListener('click', function (e) {
      var remove = e.target.closest('[data-line-remove]');
      if (remove) { change(remove.dataset.key, 0); return; }
      var step = e.target.closest('[data-line-qty]');
      if (step) {
        var line = step.closest('.line-item');
        var input = line && line.querySelector('[data-line-input]');
        if (!input) return;
        // The step is carried by data-line-qty, which reads back as
        // dataset.lineQty. Reading dataset.qty returned NaN, so the quantity
        // posted to Shopify was never a number and the line never moved.
        var stepBy = parseInt(step.dataset.lineQty, 10);
        if (isNaN(stepBy)) return;
        var val = parseInt(input.value, 10) || 1;
        change(step.dataset.key, Math.max(0, val + stepBy));
      }
    });

    root.addEventListener('change', function (e) {
      var input = e.target.closest('[data-line-input]');
      if (!input) return;
      change(input.dataset.key, Math.max(0, parseInt(input.value, 10) || 0));
    });

    var update = root.querySelector('[data-cart-update]');
    if (update) update.hidden = true;
  }

  /* --------------------------------------------------------- Checkout guard */

  // With this script running, every quantity is sent the moment it changes,
  // by line key. The cart form's updates[] fields are positional, so posting
  // them from a page older than the cart (another tab added or removed a
  // line) would hand one engraved line the quantity of another. Checkout
  // therefore leaves them out, after waiting for any change still on its way,
  // and a hidden Update submit (Enter in a quantity field) does nothing more
  // than the change already did.
  function initCheckoutGuard() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form.matches || !form.matches('[data-cart-form]')) return;
      var submitter = e.submitter;
      if (!submitter) return;

      if (submitter.name === 'update') {
        e.preventDefault();
        return;
      }

      if (submitter.name !== 'checkout') return;

      if (cartPending > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var inDrawer = !!form.closest('[data-cart-drawer]');
        var wait = window.setInterval(function () {
          if (cartPending > 0) return;
          window.clearInterval(wait);
          // The change re-rendered the cart, so the form is found again.
          var scope = document.querySelector(inDrawer ? '[data-cart-drawer]' : '[data-cart-root]');
          var button = scope && scope.querySelector('[data-cart-form] [name="checkout"]');
          if (button) button.click();
        }, 50);
        return;
      }

      Array.prototype.forEach.call(form.querySelectorAll('[data-line-input]'), function (input) {
        input.disabled = true;
      });
    }, true);

    // Coming back from checkout can restore this page from the back-forward
    // cache with the fields still switched off.
    window.addEventListener('pageshow', function () {
      Array.prototype.forEach.call(document.querySelectorAll('[data-line-input]'), function (input) {
        input.disabled = false;
      });
    });
  }

  /* ------------------------------------------------------------------ Sort */

  function initSort() {
    var sort = document.querySelector('[data-sort]');
    if (!sort) return;
    sort.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* -------------------------------------------------------- Recommendations */

  function initRecommendations() {
    var el = document.querySelector('[data-recommendations]');
    if (!el || !el.dataset.url) return;

    fetch(el.dataset.url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initReveal();
        }
      })
      .catch(function () { /* the section simply stays absent */ });
  }

  /* ---------------------------------------------------------------- Parallax */

  function initParallax() {
    if (reduced() || document.body.dataset.parallax !== 'on') return;
    var img = document.querySelector('.hero__image');
    if (!img || !('IntersectionObserver' in window)) return;

    var ticking = false;
    var active = false;

    function frame() {
      var rect = img.parentElement.getBoundingClientRect();
      var shift = Math.max(-18, Math.min(18, rect.top * -0.035));
      img.style.transform = 'translate3d(0,' + shift.toFixed(2) + 'px,0) scale(1.05)';
      ticking = false;
    }

    function onScroll() {
      if (!active || ticking) return;
      ticking = true;
      window.requestAnimationFrame(frame);
    }

    new IntersectionObserver(function (entries) {
      active = entries[0].isIntersecting;
      if (active) onScroll();
    }).observe(img);

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ------------------------------------------------------------------- Boot */

  onReady(function () {
    initReveal();
    initHeader();
    initMobileNav();
    initSearch();
    initCartPage();
    initCheckoutGuard();
    initSort();
    initRecommendations();
    initParallax();

    var pdp = document.querySelector('[data-product-root]');
    if (pdp) {
      var gallery = initGallery(pdp);
      var engraving = initEngraving(pdp);
      initSymbols(pdp, engraving);
      initQuantity(pdp);
      initVariants(pdp, gallery, engraving);
      initAddToCart(pdp, engraving);
    }
  });
})();